import { computed } from "vue";
import { BlockDef } from "./block.interfaces";
import {
    createBlock,
    textField,
    descriptiveImageField,
    headingField,
    htmlField,
} from "./blockBuilder";
import { useDefaultColors } from "./useDefaultColors";
import { ThreeColumn, type ThreeColumnProps } from "@/views/blocks/bento/bento";
import mobileImg from "@/views/blocks/bento/three-column/images/mobile.png";
import pageTopLeftImg from "@/views/blocks/bento/three-column/images/page-top-left.png";
import performanceImg from "@/views/blocks/bento/three-column/images/performance.png";
import securityImg from "@/views/blocks/bento/three-column/images/security.png";

export function useBentos() {
    const { colorDefaults } = useDefaultColors();

    const threeColumnBlock = computed<BlockDef>(() =>
        createBlock<ThreeColumnProps>({
            typeId: "ThreeColumn",
            type: "bentoGrid",
            name: "Three Column Bento Grid",
            component: ThreeColumn,
        })
            .withField("tag", textField("Tag", "Deploy faster"))
            .withField("title", headingField("Title", "Everything you need to deploy your app"))
            .withField(
                "leftColumnTitle",
                textField("Title (left column)", "Mobile friendly", "Title")
            )
            .withField(
                "leftColumnDescription",
                htmlField(
                    "Description (left column)",
                    "Responsive design that adapts to any device for a consistent experience across platforms.",
                    "Description"
                )
            )
            .withField(
                "leftColumnImage",
                descriptiveImageField("Image (left column)", {
                    src: mobileImg,
                    alt: "Mobile friendly interface",
                })
            )
            .withField(
                "topCenterColumnTitle",
                textField("Title (top center column)", "Performance", "Title")
            )
            .withField(
                "topCenterColumnDescription",
                htmlField(
                    "Description (top center column)",
                    "Optimized for speed with lightning-fast load times for exceptional user experiences.",
                    "Description"
                )
            )
            .withField(
                "topCenterColumnImage",
                descriptiveImageField("Image (top center column)", {
                    src: performanceImg,
                    alt: "Performance metrics",
                })
            )
            .withField(
                "bottomCenterColumnTitle",
                textField("Title (bottom center column)", "Security", "Title")
            )
            .withField(
                "bottomCenterColumnDescription",
                htmlField(
                    "Description (bottom center column)",
                    "Enterprise-grade protection with advanced encryption and industry compliance.",
                    "Description"
                )
            )
            .withField(
                "bottomCenterColumnImage",
                descriptiveImageField("Image (bottom center column)", {
                    src: securityImg,
                    alt: "Security features",
                })
            )
            .withField(
                "rightColumnTitle",
                textField("Title (right column)", "Powerful APIs", "Title")
            )
            .withField(
                "rightColumnDescription",
                htmlField(
                    "Description (right column)",
                    "Flexible integration options with comprehensive documentation for simple connectivity.",
                    "Description"
                )
            )
            .withField(
                "rightColumnImage",
                descriptiveImageField("Image (right column)", {
                    src: pageTopLeftImg,
                    alt: "API code example",
                })
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
                colorDefaults.value.cardBackground,
                colorDefaults.value.cardText,
            ])
            .build()
    );

    return {
        blocks: computed(() => [threeColumnBlock.value]),
    };
}
